import axiosInstance from "../../scenes/utils/axiosInstance";
import { API_ENDPOINTS } from "../../config/api";

/** Hazır formül şablonları — CreateFormula Autocomplete doğrudan bunu import eder */
export const FORMULA_TEMPLATES = [
  {
    name: "Simple Sum",
    formula: "TOTAL_SCORE",
    descriptionKey: "templateSimpleSumDescription",
    description:
      "Sum of raw points from all scored questions (e.g. 5+8+10 = 23). Excluded questions omitted.",
    required_categories: [],
    required_keys: [],
  },
  {
    name: "Average Raw Score (per question)",
    formula: "TOTAL_SCORE / QUESTION_COUNT",
    descriptionKey: "templateAverageRawScoreDescription",
    description:
      "TOTAL_SCORE ÷ count of scored questions; excluded questions not in sum or count.",
    required_categories: [],
    required_keys: [],
  },
  {
    name: "Percentage Score",
    formula: "TOTAL_PERCENT",
    /** i18n: formulas.templatePercentScoreDescription — CreateFormula resolves via descriptionKey */
    descriptionKey: "templatePercentScoreDescription",
    description:
      "Per question: (points ÷ that question's max) × 100; result is the average of those percentages (not always equal to total÷total max).",
    required_categories: [],
    required_keys: [],
  },
  {
    name: "Normalized Score (0-100)",
    formula: "TOTAL_NORMALIZED",
    descriptionKey: "templateNormalizedScoreDescription",
    description:
      "Maps each answer to 0–100 using min/max option levels per question, then averages—worst option can be 0% when min > 0.",
    required_categories: [],
    required_keys: [],
  },
  {
    name: "Custom Range (0-100)",
    formula: "(TOTAL_SCORE / TOTAL_MAX) * 100",
    descriptionKey: "templateCustomRangeDescription",
    description:
      "Total raw points achieved ÷ sum of each question's max points, × 100. One 0–100 score for the whole form (not the average of per-question %).",
    required_categories: [],
    required_keys: [],
  },
];

export default class FormulaService {
  /**
   * Create a new scoring formula
   * @param {Object} formulaData - Formula data
   * @returns {Promise} Response data
   */
  createFormula = async (formulaData) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.SCORING_FORMULAS.CREATE,
        formulaData
      );
      return response.data;
    } catch (error) {
      console.error("Error creating formula:", error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * Get all scoring formulas
   * @returns {Promise} Array of formulas
   */
  getAllFormulas = async (options = {}) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.SCORING_FORMULAS.ALL, options);
      if (options.withMeta) {
        return { data: response.data, headers: response.headers };
      }
      return response.data;
    } catch (error) {
      console.error("Error fetching formulas:", error.response?.data || error.message);
      throw error;
    }
  };

  exportFormulasExcel = async (filters = {}) => {
    const response = await axiosInstance.get(API_ENDPOINTS.SCORING_FORMULAS.EXPORT_EXCEL, {
      params: filters,
      responseType: "blob",
    });
    return response;
  };

  /**
   * Get a specific formula by ID
   * @param {string} formulaId - Formula ID
   * @returns {Promise} Formula data
   */
  getFormula = async (formulaId) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.SCORING_FORMULAS.BY_ID(formulaId)
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching formula:", error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * Update a formula
   * @param {string} formulaId - Formula ID
   * @param {Object} formulaData - Updated formula data
   * @returns {Promise} Response data
   */
  updateFormula = async (formulaId, formulaData) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.SCORING_FORMULAS.UPDATE(formulaId),
        formulaData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating formula:", error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * Delete a formula
   * @param {string} formulaId - Formula ID
   * @returns {Promise} Response data
   */
  deleteFormula = async (formulaId) => {
    try {
      const response = await axiosInstance.delete(
        API_ENDPOINTS.SCORING_FORMULAS.DELETE(formulaId)
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting formula:", error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * Test a formula with sample data
   * @param {string} expression - Formula expression
   * @param {Array} categories - Required categories
   * @param {Array} keys - Required keys
   * @returns {Promise} Test result
   */
  testFormula = async (expression, categories = [], keys = []) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.SCORING_FORMULAS.TEST, {
        formula_expression: expression,
        required_categories: categories,
        required_keys: keys,
      });
      return response.data;
    } catch (error) {
      return {
        valid: false,
        error: error.response?.data?.error || error.message || "Validation failed",
      };
    }
  };

  /**
   * Get available variables for formula creation
   * @returns {Object} Variables grouped by category
   */
  getAvailableVariables = () => {
    return {
      global: [
        {
          var: "TOTAL_SCORE",
          desc: "Sum of all question scores",
          example: "TOTAL_SCORE",
        },
        {
          var: "TOTAL_MAX",
          desc: "Maximum possible total score",
          example: "TOTAL_MAX",
        },
        {
          var: "TOTAL_PERCENT",
          desc: "Average percentage across all questions",
          example: "TOTAL_PERCENT",
        },
        {
          var: "TOTAL_NORMALIZED",
          desc: "Normalized total score (0-100)",
          example: "TOTAL_NORMALIZED",
        },
        {
          var: "QUESTION_COUNT",
          desc: "Number of questions in form",
          example: "QUESTION_COUNT",
        },
      ],
      questions: [
        {
          var: "Q[index]",
          descKey: "varDescQIndex",
          desc: "Score of question at index (1-based)",
          example: "Q1 + Q2",
        },
        {
          var: "Q[index]_PERCENT",
          descKey: "varDescQPercent",
          desc: "Percentage score of specific question",
          example: "Q1_PERCENT",
        },
        {
          var: "Q[index]_NORMALIZED",
          descKey: "varDescQNormalized",
          desc: "Normalized score of specific question",
          example: "Q1_NORMALIZED",
        },
      ],
      categories: [
        {
          var: "CATEGORY[name]",
          desc: "Sum of scores in category",
          example: "CATEGORY[Pain]",
        },
        {
          var: "CATEGORY_MAX",
          desc: "Maximum possible score in category",
          example: "CATEGORY_MAX",
        },
        {
          var: "CATEGORY[name]_PERCENT",
          desc: "Percentage of category score",
          example: "CATEGORY[Pain]_PERCENT",
        },
        {
          var: "CATEGORY[name]_NORMALIZED",
          desc: "Normalized category score",
          example: "CATEGORY[Pain]_NORMALIZED",
        },
      ],
      operators: [
        { var: "+", desc: "Addition", example: "Q1 + Q2" },
        { var: "-", desc: "Subtraction", example: "Q1 - Q2" },
        { var: "*", desc: "Multiplication", example: "Q1 * 2" },
        { var: "/", desc: "Division", example: "Q1 / 2" },
        { var: "()", desc: "Parentheses", example: "(Q1 + Q2) * 2" },
      ],
    };
  };

  /**
   * Get formula templates for quick start
   * @returns {Array} Formula templates
   */
  getFormulaTemplates = () => FORMULA_TEMPLATES;

  /**
   * Validate formula expression (frontend validation)
   * @param {string} expression - Formula expression
   * @returns {Object} Validation result
   */
  validateFormulaExpression = (expression) => {
    if (!expression || expression.trim() === "") {
      return { valid: false, error: "Formula expression cannot be empty" };
    }

    // Check for balanced parentheses
    let balance = 0;
    for (let char of expression) {
      if (char === "(") balance++;
      if (char === ")") balance--;
      if (balance < 0) {
        return { valid: false, error: "Unbalanced parentheses" };
      }
    }
    if (balance !== 0) {
      return { valid: false, error: "Unbalanced parentheses" };
    }

    // Check for invalid characters
    const validPattern = /^[Q\[\]TOTAL_SCOREMAXPRNLZDUESTION_CTGRYCATEGORY_0-9\+\-\*\/\(\)\s\.]+$/i;
    if (!validPattern.test(expression)) {
      return { valid: false, error: "Invalid characters in formula" };
    }

    // Check for consecutive operators
    if (/[\+\-\*\/]{2,}/.test(expression.replace(/\s/g, ""))) {
      return { valid: false, error: "Consecutive operators are not allowed" };
    }

    return { valid: true };
  };
}
